'use client';
import { useEffect, useState } from 'react';
import type { Idea } from '@/lib/research/state-machine';
import { deriveRating } from '@/lib/research/state-machine';
import { useResource } from '@/lib/use-resource';
import type { VelocityRow, FitRow } from '@/lib/research/types';
import { compositeTier as sharedCompositeTier } from '@/lib/composite-tiers';
import { qualityVerbal, valuationVerbal } from '@/lib/composite-score';
import { PortfolioFitPanel } from './PortfolioFitPanel';
import { ScoreRing } from '../stock-detail/ScoreRing';
import { Section, Stat, ConvictionStat, ProseSection } from './CardSections';

interface IdeaImpact {
  symbol: string;
  before: { expectedReturn: number; expectedStdev: number; expectedSharpe: number; hhi: number };
  after: { expectedReturn: number; expectedStdev: number; expectedSharpe: number; hhi: number };
  deltas: { expectedReturn: number; expectedStdev: number; expectedSharpe: number; composite: number; topSectorWeight: number; hhi: number };
}

interface Props {
  idea: Idea;
  velocity: VelocityRow | null;
  fit: FitRow | null;
  swapSourceVelocity: VelocityRow | null;
  historical?: boolean; // when true, no live alerts fetch + no trajectory chart
}

const RATING_COLORS: Record<string, string> = {
  BUY: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  HOLD: 'bg-[#1e293b] text-[#cbd5e1] border-[#334155]',
  SELL: 'bg-red-500/15 text-red-300 border-red-500/40',
};

interface Event { ts: string; tier: string; type: string; symbol: string; headline: string }

const STATUS_COLORS: Record<string, string> = {
  initiating: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  reiterating: 'bg-[#1e293b]/60 text-[#94a3b8] border-[#334155]',
  upgrading: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  downgrading: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'closing-paid-off': 'bg-emerald-600/20 text-emerald-300 border-emerald-600/40',
  'closing-violated': 'bg-red-600/20 text-red-300 border-red-600/40',
};

const CONVICTION_COLORS: Record<string, string> = {
  low: 'text-[#94a3b8]',
  med: 'text-amber-400',
  high: 'text-emerald-400',
};

const tierColor: Record<string, string> = {
  'Q-direct': 'text-amber-400',
  'Q-flow': 'text-blue-400',
  'FYI': 'text-[#94a3b8]',
};

export function IdeaCard({ idea, velocity, fit, swapSourceVelocity, historical = false }: Props) {
  // Historical view starts with [] so we never trigger a fetch.
  const [events, setEvents] = useState<Event[] | null>(() => (historical ? [] : null));
  // Recommendation body collapsed by default — header acts as the toggle.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (historical) return;
    let cancelled = false;
    fetch(`/api/research-events?symbol=${idea.symbol}&days=14`)
      .then(r => r.json())
      .then((d: { events: Event[] }) => { if (!cancelled) setEvents(d.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => { cancelled = true; };
  }, [idea.symbol, historical]);

  // Per-idea portfolio-impact deltas from the snapshot. Shared via useResource
  // so multiple IdeaCards on the same page coalesce. Derived at render time —
  // no setImpact-in-effect needed.
  const { data: snapshot } = useResource<{ ideas: IdeaImpact[] }>(
    historical ? null : '/api/portfolio-snapshot',
  );
  const impact = historical ? null : (snapshot?.ideas?.find((i) => i.symbol === idea.symbol) ?? null);

  const lastNote = idea.notes[idea.notes.length - 1];
  if (!lastNote) return null;
  const rating = deriveRating(idea);

  // Composite score for the header — prefer live velocity, fall back to last note's snapshot.
  // Tier vocabulary unified 2026-04-30: STRONG BUY / BUY / HOLD / SELL via
  // shared compositeTier(). No more "...composite" suffix — the tier label is
  // a recommendation, not a numeric description.
  const headerComposite = velocity?.composite ?? lastNote.drivers.composite;
  const tier = headerComposite != null
    ? (() => { const t = sharedCompositeTier(headerComposite); return { label: t.label, color: t.textClass }; })()
    : null;

  return (
    <article className="bg-[#0d1117] border border-[#1e293b] rounded-lg overflow-hidden">
      {/* Header — also the collapse toggle. Click anywhere on it to open/close
          the full recommendation body. */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left px-6 py-4 border-b border-[#1e293b] bg-gradient-to-r from-[#0d1117] to-[#111827] hover:bg-[#111827] transition-colors cursor-pointer"
        aria-expanded={expanded}
      ><div>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-[13px] font-bold tracking-[0.15em] px-3 py-1 rounded border ${RATING_COLORS[rating]}`}>
              {rating}
            </span>
            <div className="flex flex-col">
              <h2 className="text-2xl font-bold text-white tracking-tight leading-tight">{idea.symbol}</h2>
              <span className="text-[12px] text-[#94a3b8] leading-tight">{idea.name}</span>
            </div>
            {headerComposite != null && tier ? (
              <span className="flex items-baseline gap-1.5 ml-1">
                <span className="text-[10px] uppercase tracking-wide text-[#64748b]">composite</span>
                <span className={`text-[15px] font-bold tabular-nums ${tier.color}`}>{headerComposite.toFixed(2)}</span>
                <span className={`text-[10px] uppercase tracking-wide ${tier.color}`}>{tier.label}</span>
              </span>
            ) : null}
            <span className="text-[11px] text-[#475569] tracking-wide uppercase">{idea.sector}</span>
          </div>
          <div className="flex items-baseline gap-2 text-[11px]">
            <span className={`px-2 py-0.5 rounded border tracking-wide uppercase font-semibold ${STATUS_COLORS[lastNote.status] || ''}`}>
              {lastNote.status.replace('-', ' ')}
            </span>
            <span className="text-[#64748b]">conviction</span>
            <span className={`font-semibold uppercase ${CONVICTION_COLORS[idea.conviction] || ''}`}>
              {idea.conviction}
            </span>
          </div>
        </div>

        {/* Action banner — type + capital source */}
        <div className="mt-3 text-[12px] flex items-baseline gap-2 flex-wrap">
          {idea.type === 'add' ? (
            <>
              <span className="text-[10px] uppercase tracking-wide text-blue-400 font-semibold">Add</span>
              <span className="text-[#94a3b8]">New position. Funded from cash or pro-rata trim.</span>
            </>
          ) : (
            <>
              <span className="text-[10px] uppercase tracking-wide text-amber-400 font-semibold">Swap</span>
              <span className="text-[#e2e8f0]">
                Source capital from{' '}
                <span className="inline-flex items-baseline gap-1.5">
                  <span className="font-mono font-semibold text-amber-400">{idea.swapSource}</span>
                  <span className="text-[10px] font-bold tracking-[0.15em] px-1.5 py-0.5 rounded border bg-red-500/15 text-red-300 border-red-500/40">
                    SELL
                  </span>
                </span>
                {swapSourceVelocity ? (
                  <span className="text-[#64748b]">
                    {' '}(composite {swapSourceVelocity.composite.toFixed(2)} → {idea.symbol} {velocity?.composite.toFixed(2)},
                    {' '}+{((velocity?.composite ?? 0) - swapSourceVelocity.composite).toFixed(2)})
                  </span>
                ) : null}
              </span>
            </>
          )}
          <span className="text-[#475569] ml-auto flex items-center gap-2">
            Initiated {idea.initiationDate} · Last note {idea.lastNoteDate}
            <span className={`text-[#94a3b8] text-[14px] transition-transform ${expanded ? 'rotate-90' : ''}`}>›</span>
          </span>
        </div>
      </div></button>

      {/* Body — overnight-memo format adopted 2026-05-04 so every research
          surface reads as the same kind of document. Stock Data first, then
          Portfolio Impact, then the prose institutional note, then the
          deterministic pitch + recent alerts. PM-lens "Addressing the Room"
          intentionally not rendered (Steve directive).
          Collapsed by default — click the header to open. */}
      {expanded && <div className="p-6">
        {/* === Stock Data — Q/V/Composite rings + ΔSharpe/Action/Conviction === */}
        <Section heading="Stock Data">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4 py-2">
            <div className="flex items-center gap-6">
              <ScoreRing
                score={velocity?.q ?? null}
                label="Quality"
                verbal={qualityVerbal(velocity?.q ?? null).word}
                verbalColor={qualityVerbal(velocity?.q ?? null).color}
              />
              <ScoreRing
                score={velocity?.v ?? null}
                label="Valuation"
                verbal={valuationVerbal(velocity?.v ?? null).word}
                verbalColor={valuationVerbal(velocity?.v ?? null).color}
              />
              <ScoreRing
                score={headerComposite ?? null}
                label="Composite"
                verbal={headerComposite != null ? sharedCompositeTier(headerComposite).label : undefined}
                verbalColor={headerComposite != null ? sharedCompositeTier(headerComposite).hex : undefined}
              />
            </div>
            <div className="flex items-center gap-3">
              <Stat
                label="ΔSharpe"
                value={impact?.deltas.expectedSharpe != null
                  ? `${impact.deltas.expectedSharpe >= 0 ? '+' : ''}${impact.deltas.expectedSharpe.toFixed(3)}`
                  : '—'}
              />
              <Stat
                label="Action"
                value={idea.type === 'add' ? 'ADD' : 'SWAP'}
                hint={idea.type === 'add' ? '5% target' : `from ${idea.swapSource ?? '?'}`}
                highlight
              />
              <ConvictionStat conviction={idea.conviction} />
            </div>
          </div>
        </Section>

        {/* === Portfolio Impact === */}
        <Section heading="Portfolio Impact">
          {impact ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px] mb-4">
              <Impact label="Expected Return" value={impact.deltas.expectedReturn} fmt="pct" preferGreater />
              <Impact label="Volatility" value={impact.deltas.expectedStdev} fmt="pct" preferLesser />
              <Impact label="Risk-Adjusted Return" value={impact.deltas.expectedSharpe} fmt="num2" preferGreater />
              <Impact label="Composite Score" value={impact.deltas.composite} fmt="num2" preferGreater />
              <Impact label="Concentration" value={impact.deltas.hhi} fmt="num3" preferLesser />
            </div>
          ) : (
            <div className="text-[11px] text-[#64748b] italic mb-4">
              Snapshot impact data not yet computed for this candidate.
            </div>
          )}
          <PortfolioFitPanel fit={fit} inPortfolio={false} />
        </Section>

        {/* === Sell-side prose — same ordering as the overnight memo === */}
        <Section heading="The Industry"><ProseSection body={lastNote.industryContext} /></Section>
        <Section heading="The Company"><ProseSection body={lastNote.companyOverview} /></Section>
        <Section heading="Leadership"><ProseSection body={lastNote.leadership} /></Section>
        <Section heading="Competitive Position"><ProseSection body={lastNote.competitivePosition} /></Section>
        <Section heading="The Thesis"><ProseSection body={lastNote.theThesis} /></Section>
        <Section heading="How It Fits Your Book"><ProseSection body={lastNote.bookFit} /></Section>
        <Section heading="The Bull Case"><ProseSection body={lastNote.bullCase ?? lastNote.whyOwnIt} /></Section>
        <Section heading="The Bear Case"><ProseSection body={lastNote.bearCase ?? lastNote.downsideRisk} /></Section>
        <Section heading="The Ask"><ProseSection body={lastNote.theAsk ?? lastNote.expectedReturn} /></Section>

        {/* === Deterministic data-driven pitch === */}
        <Section heading="The Pitch">
          <PitchPoints
            idea={idea}
            velocity={velocity}
            fit={fit}
            swapSourceVelocity={swapSourceVelocity}
            historicalNote={historical ? lastNote : null}
          />
        </Section>

        {lastNote.whatChangedToday ? (
          <Section heading="Today's Update">
            <div className="text-[12px] text-[#cbd5e1] leading-relaxed">{lastNote.whatChangedToday}</div>
          </Section>
        ) : null}

        {events && events.length > 0 ? (
          <Section heading="Recent Alerts">
            <ul className="space-y-1 text-[12px]">
              {events.map((e, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[#64748b] tabular-nums whitespace-nowrap">{e.ts.slice(0, 10)}</span>
                  <span className={`uppercase tracking-wide text-[10px] font-semibold ${tierColor[e.tier] || 'text-[#94a3b8]'} whitespace-nowrap`}>
                    {e.tier}
                  </span>
                  <span className="text-[#94a3b8]">{e.headline}</span>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}
      </div>}
    </article>
  );
}

// Auto-generated point-form pitch from the data the desk has on hand. No prose,
// no adjectives the model can't justify with a number. Cohen/Asness format.
function PitchPoints({
  idea,
  velocity,
  fit,
  swapSourceVelocity,
  historicalNote,
}: {
  idea: Idea;
  velocity: VelocityRow | null;
  fit: FitRow | null;
  swapSourceVelocity: VelocityRow | null;
  historicalNote: NonNullable<Idea['notes'][number]> | null;
}) {
  const points: { label: string; value: string; tone?: 'good' | 'bad' | 'neutral' }[] = [];

  const composite = velocity?.composite ?? historicalNote?.drivers.composite ?? null;
  const q = velocity?.q ?? null;
  const v = velocity?.v ?? null;
  const accel = velocity?.accel5 ?? historicalNote?.drivers.accel5 ?? null;
  const streak = velocity?.alphaStreak ?? historicalNote?.drivers.alphaStreak ?? 0;
  // Historical fallback: composite Δ5d ≈ qΔ5d + vΔ5d
  const d5 = velocity?.d5.dcomp
    ?? (historicalNote?.drivers.qDelta5d != null && historicalNote?.drivers.vDelta5d != null
      ? historicalNote.drivers.qDelta5d + historicalNote.drivers.vDelta5d
      : null);
  const d20 = velocity?.d20.dcomp ?? null;

  // Composite + tier — uses shared STRONG BUY / BUY / HOLD / SELL vocabulary.
  if (composite != null) {
    const tierLabel = sharedCompositeTier(composite).label;
    points.push({
      label: 'Composite',
      value: `${composite.toFixed(2)} — ${tierLabel}`,
      tone: composite >= 3 ? 'good' : composite < 1.5 ? 'bad' : 'neutral',
    });
  }

  // Quality — plain language
  if (q != null) {
    const desc = q >= 1.5 ? 'very high quality'
      : q >= 0.5 ? 'high quality'
      : q >= -0.5 ? 'average quality'
      : 'low quality';
    points.push({
      label: 'Quality',
      value: `${q >= 0 ? '+' : ''}${q.toFixed(2)} — ${desc}`,
      tone: q >= 0.5 ? 'good' : q < 0 ? 'bad' : 'neutral',
    });
  }

  // Valuation — plain language (cheap / fairly priced / expensive)
  if (v != null) {
    const desc = v >= 1.5 ? 'very cheap'
      : v >= 0.5 ? 'cheap'
      : v >= -0.5 ? 'fairly priced'
      : v >= -1.5 ? 'expensive'
      : 'very expensive';
    points.push({
      label: 'Valuation',
      value: `${v >= 0 ? '+' : ''}${v.toFixed(2)} — ${desc}`,
      tone: v >= 0.5 ? 'good' : v < -1.0 ? 'bad' : 'neutral',
    });
  }

  // Direction — plain language (improving / getting worse / stable)
  if (d5 != null || d20 != null) {
    const parts: string[] = [];
    if (d5 != null) parts.push(`5-day ${d5 >= 0 ? '+' : ''}${d5.toFixed(2)}`);
    if (d20 != null) parts.push(`20-day ${d20 >= 0 ? '+' : ''}${d20.toFixed(2)}`);
    const dir = (d5 ?? 0) > 0.5 && (accel ?? 0) > 0
      ? 'improving fast'
      : (d5 ?? 0) > 0
      ? 'improving'
      : (d5 ?? 0) < -0.5
      ? 'getting worse'
      : 'stable';
    points.push({
      label: 'Direction',
      value: `Composite change ${parts.join(' · ')} — ${dir}`,
      tone: (d5 ?? 0) > 0 && (accel ?? 0) > 0 ? 'good' : (d5 ?? 0) < -0.5 ? 'bad' : 'neutral',
    });
  }

  // Streak — top-10 days
  if (streak >= 1) {
    points.push({
      label: 'Streak',
      value: `${streak} day${streak === 1 ? '' : 's'} in the top 10 — auto-close at 5`,
      tone: streak >= 4 ? 'bad' : 'neutral',
    });
  }

  // Fit / β / sector
  if (fit) {
    const fitParts: string[] = [];
    if (fit.betaSpy != null) {
      const bdesc = fit.betaSpy < 0.7 ? 'low-β defensive'
        : fit.betaSpy < 1.05 ? 'roughly market-like'
        : 'high-β cyclical';
      fitParts.push(`β ${fit.betaSpy.toFixed(2)} (${bdesc})`);
    }
    if (fit.sectorConcDelta != null) {
      fitParts.push(`sector Δ ${fit.sectorConcDelta >= 0 ? '+' : ''}${fit.sectorConcDelta.toFixed(1)}pp`);
    }
    if (fit.maxCorr) {
      fitParts.push(`max corr ${fit.maxCorr.value.toFixed(2)} with ${fit.maxCorr.symbol}`);
    }
    if (fitParts.length) {
      points.push({
        label: 'Portfolio fit',
        value: fitParts.join(' · '),
        tone: fit.flags?.highlyCorrelated ? 'bad' : fit.flags?.sectorOverweight ? 'bad' : 'neutral',
      });
    }
  }

  // Action / sizing
  if (idea.type === 'add') {
    points.push({
      label: 'Action',
      value: 'ADD at 5% target — funded from cash or pro-rata trim of full positions',
      tone: 'neutral',
    });
  } else {
    const sourceComp = swapSourceVelocity?.composite;
    const candComp = velocity?.composite;
    const upgrade = sourceComp != null && candComp != null ? `+${(candComp - sourceComp).toFixed(2)}` : '?';
    points.push({
      label: 'Action',
      value: `SWAP from ${idea.swapSource} (composite upgrade ${upgrade}) — must clear ≥0.20 to qualify`,
      tone: 'neutral',
    });
  }

  // Risk / what would close it
  points.push({
    label: 'Risk',
    value: 'Auto-close on 5 alpha-zone days OR a -1.0+ composite hit. 30-day quarantine after closed-violated.',
    tone: 'neutral',
  });

  return (
    <ul className="space-y-1.5 text-[13px]">
      {points.map((p, i) => {
        const tone = p.tone === 'good' ? 'text-emerald-400'
          : p.tone === 'bad' ? 'text-red-400'
          : 'text-[#cbd5e1]';
        return (
          <li key={i} className="flex gap-3 leading-snug">
            <span className="text-amber-400/70 select-none">•</span>
            <span className="text-[#64748b] uppercase tracking-wide text-[10px] font-semibold w-[78px] flex-shrink-0 pt-0.5">{p.label}</span>
            <span className={tone}>{p.value}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Impact({ label, value, fmt, preferGreater, preferLesser }: {
  label: string;
  value: number;
  fmt: 'pct' | 'num2' | 'num3';
  preferGreater?: boolean;
  preferLesser?: boolean;
}) {
  const sign = value >= 0 ? '+' : '';
  const formatted = fmt === 'pct'
    ? `${sign}${(value * 100).toFixed(2)}%`
    : fmt === 'num3'
    ? `${sign}${value.toFixed(3)}`
    : `${sign}${value.toFixed(2)}`;
  const tone = value === 0
    ? 'text-[#94a3b8]'
    : preferGreater
    ? value > 0 ? 'text-emerald-400' : 'text-red-400'
    : preferLesser
    ? value < 0 ? 'text-emerald-400' : 'text-red-400'
    : 'text-[#cbd5e1]';
  return (
    <div>
      <div className="text-[#64748b] uppercase tracking-wide text-[10px]">{label}</div>
      <div className={`text-[14px] tabular-nums font-semibold ${tone}`}>{formatted}</div>
    </div>
  );
}
